import { Link } from "react-router-dom";
import { motion, useReducedMotion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { Phone, ArrowRight, Compass, ShieldCheck, FileText, Handshake, Check } from "lucide-react";
import { useSite } from "@/context/SiteContext";
import { track } from "@/lib/api";
import Reveal, { MaskedLine } from "@/components/Reveal";
import SEO from "@/components/SEO";
import CompactQuoteForm from "@/components/forms/CompactQuoteForm";
import FAQAccordion from "@/components/FAQAccordion";
import CTASection from "@/components/CTASection";
import AgentProfile from "@/components/AgentProfile";
import CarrierGrid, { CarrierMarquee } from "@/components/CarrierGrid";
import {
  TRUST_CARDS, STEPS, PRODUCTS, FINAL_EXPENSE_USES, FINAL_EXPENSE_SUMMARY, FAQ_HOME, PRODUCT_DISCLOSURE,
} from "@/data/siteContent";

const ICONS = { Compass, ShieldCheck, FileText, Handshake };

function Hero() {
  const site = useSite();
  const reduce = useReducedMotion();
  const imgRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: imgRef, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], reduce ? [0, 0] : [40, -40]);

  return (
    <section className="relative overflow-hidden" data-testid="hero-section">
      <div className="absolute inset-0 bg-gradient-to-br from-navy-50 via-cream to-cream" aria-hidden="true" />
      <div className="absolute -top-24 -right-24 h-96 w-96 rounded-full bg-gold/15 blur-3xl" aria-hidden="true" />
      <div className="container-site relative grid gap-12 py-16 md:py-24 lg:grid-cols-[minmax(0,11fr)_minmax(0,9fr)] lg:gap-16">
        <div>
          <Reveal>
            <p className="eyebrow" data-testid="hero-eyebrow">Final Expense & Life Insurance Guidance</p>
          </Reveal>
          <h1 className="mt-4 text-4xl sm:text-5xl lg:text-6xl tracking-tight" data-testid="hero-headline">
            <MaskedLine delay={0.05}>Life Insurance Made</MaskedLine>
            <MaskedLine delay={0.18}><span className="text-gold-dark italic">Personal</span> and Simple</MaskedLine>
          </h1>
          <Reveal delay={0.35}>
            <p className="mt-6 text-lg leading-relaxed text-mutedink max-w-xl" data-testid="hero-subtext">
              Protect the people you love with coverage selected for your needs, health, and budget. Michael Janas
              helps you compare life insurance options from established carriers and understand what you are
              buying — without unnecessary pressure.
            </p>
          </Reveal>
          <Reveal delay={0.48}>
            <div className="mt-8 flex flex-col sm:flex-row gap-4">
              <Link to="/quote" className="btn-accent" data-testid="hero-quote-button"
                onClick={() => track("cta_click", { location: "hero", action: "get_quote" })}>
                Get My Free Quote <ArrowRight className="h-5 w-5" aria-hidden="true" />
              </Link>
              <a href={`tel:${site.phone_href}`} className="btn-outline" data-testid="hero-call-button"
                onClick={() => track("phone_click", { location: "hero" })}>
                <Phone className="h-5 w-5" aria-hidden="true" /> Call Michael
              </a>
            </div>
            <p className="mt-6 text-sm font-semibold text-navy/80 tracking-wide" data-testid="hero-trust-line">
              Personal guidance &nbsp;•&nbsp; Multiple carrier options &nbsp;•&nbsp; No-cost consultation
            </p>
          </Reveal>
          <Reveal delay={0.55} className="mt-10 hidden lg:block">
            <div ref={imgRef} className="relative overflow-hidden rounded-2xl shadow-lg">
              <motion.img
                src="https://images.unsplash.com/photo-1761839257647-df30867afd54?w=1200&q=80&auto=format&fit=crop"
                alt="An older couple sitting together outdoors, relaxed and smiling"
                className="h-64 w-full object-cover"
                style={{ y }}
                loading="lazy"
                data-testid="hero-image"
              />
              <div className="absolute inset-0 bg-navy/10" aria-hidden="true" />
            </div>
          </Reveal>
        </div>
        <Reveal delay={0.3} className="lg:pt-4">
          <CompactQuoteForm />
        </Reveal>
      </div>
    </section>
  );
}

function TrustCards() {
  return (
    <section className="py-20 md:py-28" data-testid="trust-section">
      <div className="container-site">
        <Reveal>
          <h2 className="text-3xl sm:text-4xl tracking-tight max-w-2xl">A Different Kind of Insurance Conversation</h2>
        </Reveal>
        <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {TRUST_CARDS.map((c, i) => {
            const Icon = ICONS[c.icon];
            return (
              <Reveal key={c.title} delay={i * 0.08}>
                <div className="h-full rounded-xl border border-navy-100/70 bg-white p-7 shadow-sm transition-shadow duration-300 hover:shadow-md"
                  data-testid={`trust-card-${i}`}>
                  <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-navy-50 text-navy">
                    <Icon className="h-6 w-6" aria-hidden="true" />
                  </div>
                  <h3 className="mt-5 text-xl">{c.title}</h3>
                  <p className="mt-2.5 text-mutedink leading-relaxed">{c.text}</p>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function FinalExpenseFeature() {
  return (
    <section className="bg-white border-y border-navy-100/60 py-20 md:py-28" data-testid="final-expense-feature">
      <div className="container-site grid gap-14 lg:grid-cols-2 lg:gap-20">
        <Reveal>
          <p className="eyebrow">Our Primary Focus</p>
          <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl tracking-tight">Help Protect Your Family From Final Expenses</h2>
          <p className="mt-6 text-lg text-mutedink leading-relaxed">
            Final expense insurance is generally a form of whole life insurance intended to help your beneficiaries
            handle the costs that come at the end of a life — so your family can focus on each other, not on bills.
          </p>
          <p className="mt-4 font-semibold text-navy">Benefits are commonly used for:</p>
          <ul className="mt-4 grid gap-2.5 sm:grid-cols-2">
            {FINAL_EXPENSE_USES.map((u) => (
              <li key={u} className="flex items-start gap-2.5 text-mutedink">
                <Check className="mt-1 h-5 w-5 shrink-0 text-gold-dark" aria-hidden="true" /> {u}
              </li>
            ))}
          </ul>
          <div className="mt-8 flex flex-col sm:flex-row gap-4">
            <Link to="/final-expense" className="btn-primary" data-testid="fe-learn-button">Learn About Final Expense</Link>
            <Link to="/quote" className="btn-accent" data-testid="fe-quote-button"
              onClick={() => track("cta_click", { location: "final_expense_feature", action: "get_quote" })}>
              Request a Final Expense Quote
            </Link>
          </div>
        </Reveal>
        <Reveal delay={0.15}>
          <div className="rounded-2xl border border-gold/40 bg-cream p-8 shadow-sm" data-testid="fe-summary-card">
            <h3 className="text-2xl">At a Glance</h3>
            <ul className="mt-6 space-y-4">
              {FINAL_EXPENSE_SUMMARY.map((s) => (
                <li key={s} className="flex items-start gap-3 text-lg text-ink">
                  <span className="mt-2 h-2 w-2 shrink-0 rounded-full bg-gold-dark" aria-hidden="true" /> {s}
                </li>
              ))}
            </ul>
            <p className="mt-8 border-t border-gold/30 pt-5 text-sm text-mutedink leading-relaxed">
              {PRODUCT_DISCLOSURE}
            </p>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function HowItWorks() {
  return (
    <section className="bg-mist py-20 md:py-28" data-testid="how-it-works">
      <div className="container-site">
        <Reveal>
          <p className="eyebrow">The Process</p>
          <h2 className="mt-3 text-3xl sm:text-4xl tracking-tight">How Working Together Works</h2>
        </Reveal>
        <div className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
          {STEPS.map((s, i) => (
            <Reveal key={s.n} delay={i * 0.08}>
              <div className="h-full rounded-xl bg-white p-7 shadow-sm border border-navy-100/60" data-testid={`step-card-${i}`}>
                <span className="font-serif text-4xl text-gold-dark" aria-hidden="true">{s.n}</span>
                <h3 className="mt-4 text-xl">{s.title}</h3>
                <p className="mt-2.5 text-mutedink leading-relaxed">{s.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function ProductCards() {
  return (
    <section className="py-20 md:py-28" data-testid="products-section">
      <div className="container-site">
        <Reveal>
          <p className="eyebrow">Coverage Options</p>
          <h2 className="mt-3 text-3xl sm:text-4xl tracking-tight max-w-2xl">Life Insurance for the Way Real Life Works</h2>
        </Reveal>
        <div className="mt-12 grid gap-6 lg:grid-cols-3">
          {PRODUCTS.map((p, i) => (
            <Reveal key={p.title} delay={i * 0.08}>
              <div className="flex h-full flex-col rounded-xl border border-navy-100/70 bg-white p-8 shadow-sm transition-shadow duration-300 hover:shadow-md"
                data-testid={`product-card-${i}`}>
                <h3 className="text-2xl">{p.title}</h3>
                <ul className="mt-5 flex-1 space-y-3">
                  {p.points.map((pt) => (
                    <li key={pt} className="flex items-start gap-2.5 text-mutedink leading-relaxed">
                      <Check className="mt-1 h-5 w-5 shrink-0 text-gold-dark" aria-hidden="true" /> {pt}
                    </li>
                  ))}
                </ul>
                <div className="mt-7 flex flex-col gap-3">
                  <Link to={p.link} className="btn-primary !py-3 !text-base w-full" data-testid={`product-learn-${i}`}>Learn More</Link>
                  <Link to="/quote" className="btn-outline !py-3 !text-base w-full" data-testid={`product-quote-${i}`}
                    onClick={() => track("cta_click", { location: "product_card", action: "get_quote", product: p.title })}>
                    Get a Quote
                  </Link>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function IndependentAgent() {
  return (
    <section className="relative overflow-hidden bg-navy py-20 md:py-28" data-testid="independent-agent-section">
      <div className="absolute inset-0 opacity-[0.05]" aria-hidden="true"
        style={{ backgroundImage: "radial-gradient(circle at 80% 20%, #C5A059 0, transparent 45%)" }} />
      <div className="container-site relative grid items-center gap-12 lg:grid-cols-2">
        <Reveal>
          <h2 className="!text-cream text-3xl sm:text-4xl lg:text-5xl tracking-tight">Your Needs Come Before Any One Insurance Company</h2>
          <p className="mt-6 text-lg text-cream/85 leading-relaxed">
            Insurance companies evaluate age, health, medications, and medical history differently. As an independent
            agent, Michael can help identify available options and explain the differences so you can make an
            informed choice.
          </p>
        </Reveal>
        <Reveal delay={0.15}>
          <ul className="grid gap-4 sm:grid-cols-2">
            {[
              "Access to more than one insurance carrier",
              "Help evaluating underwriting differences",
              "Options suited to different health histories",
              "Plain-language comparisons",
              "Personalized assistance",
              "No separate fee to request a quote or talk",
            ].map((t) => (
              <li key={t} className="flex items-start gap-3 rounded-lg bg-white/5 border border-white/10 p-4 text-cream/90">
                <Check className="mt-0.5 h-5 w-5 shrink-0 text-gold" aria-hidden="true" /> {t}
              </li>
            ))}
          </ul>
        </Reveal>
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <>
      <SEO
        title="1907 Insurance Group | Final Expense & Life Insurance — (877) 833-1907"
        description="Independent licensed agent Michael Janas helps you compare final expense, term, and whole life insurance from established carriers. No pressure, no obligation. Call (877) 833-1907."
      />
      <Hero />
      <TrustCards />
      <FinalExpenseFeature />
      <HowItWorks />
      <ProductCards />
      <IndependentAgent />
      <section className="py-20 md:py-28" data-testid="carriers-section">
        <div className="container-site">
          <Reveal>
            <p className="eyebrow">Where Coverage Comes From</p>
            <h2 className="mt-3 text-3xl sm:text-4xl tracking-tight max-w-2xl">Coverage Options From Established Insurance Companies</h2>
          </Reveal>
          <div className="mt-12">
            <CarrierGrid />
          </div>
        </div>
      </section>
      <CarrierMarquee />
      <section className="py-20 md:py-28" data-testid="about-preview-section">
        <div className="container-site">
          <AgentProfile />
        </div>
      </section>
      <section className="bg-mist py-20 md:py-28" data-testid="faq-preview-section">
        <div className="container-site grid gap-12 lg:grid-cols-[minmax(0,2fr)_minmax(0,3fr)]">
          <Reveal>
            <p className="eyebrow">Common Questions</p>
            <h2 className="mt-3 text-3xl sm:text-4xl tracking-tight">Honest Answers, in Plain Language</h2>
            <p className="mt-5 text-lg text-mutedink leading-relaxed">
              Specific answers always depend on the insurer and the policy — but these will give you a solid start.
            </p>
            <Link to="/faqs" className="btn-outline mt-8" data-testid="faq-preview-link">See All FAQs</Link>
          </Reveal>
          <Reveal delay={0.12}>
            <FAQAccordion items={FAQ_HOME.slice(0, 6)} testid="home-faq" />
          </Reveal>
        </div>
      </section>
      <CTASection />
    </>
  );
}
